import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Clock, Loader2 } from 'lucide-react';
import { PublicLayout, Section } from '@/components/landing/PublicLayout';
import { PricingSection, Markdown } from '@/components/landing/Blocks';
import { useLanding, youtubeEmbed } from '@/lib/landing';
import { useLang } from '@/lib/i18n';
import { useCopy, tf } from '@/lib/landingCopy';
import { useSeo } from '@/lib/seo';
import { api } from '@/lib/api';

export function PricingPage() {
  const d = useLanding();
  const { lang } = useLang();
  const c = useCopy(lang);
  useSeo({ title: c.seo_pricing_title, description: c.seo_pricing_desc, path: '/pricing', lang });
  return (
    <PublicLayout>
      <Section eyebrow={c.pricing_eyebrow} title={c.pricing_page_title} desc={c.pricing_page_desc} testid="pricing-page">
        <PricingSection data={d} />
      </Section>
      <section className="bg-white border-t border-border/60">
        <Section eyebrow={c.faq_eyebrow} title={c.faq_title} testid="pricing-faq">
          <div className="grid gap-4 md:grid-cols-2">{c.faq.map(([q, a]) => <div key={q} className="rounded-2xl border border-border bg-[hsl(var(--pillot-ivory))] p-6"><h3 className="font-display font-semibold">{q}</h3><p className="mt-2 text-sm text-muted-foreground leading-relaxed">{a}</p></div>)}</div>
        </Section>
      </section>
    </PublicLayout>
  );
}

export function LibraryPage() {
  const d = useLanding();
  const { lang } = useLang();
  const c = useCopy(lang);
  useSeo({ title: c.seo_library_title, description: c.seo_library_desc, path: '/library', lang });
  const [cat, setCat] = useState('__all');
  const tutorials = d?.tutorials || [];
  const cats = Array.from(new Set(tutorials.map((t) => t.category || 'Umum')));
  const list = cat === '__all' ? tutorials : tutorials.filter((t) => (t.category || 'Umum') === cat);
  return (
    <PublicLayout>
      <Section eyebrow={c.lib_eyebrow} title={c.lib_title} desc={c.lib_desc} testid="library-page">
        {!d ? <Loader2 className="animate-spin text-primary" /> : (
          <>
            <div className="flex flex-wrap gap-2 mb-8" data-testid="library-categories">{[['__all', c.lib_all], ...cats.map((x) => [x, x])].map(([k, l]) => <button key={k} type="button" onClick={() => setCat(k)} className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${cat === k ? 'border-primary bg-primary text-white' : 'border-border bg-white hover:border-primary/40'}`} data-testid={`library-cat-${k === '__all' ? 'Semua' : k}`}>{l}</button>)}</div>
            {list.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="library-empty">{c.lib_empty}</p> : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {list.map((t) => (
                  <Link key={t.id} to={`/library/${t.slug}`} className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-white shadow-soft transition-all hover:-translate-y-1" data-testid={`tutorial-card-${t.slug}`}>
                    <div className="aspect-[16/9] bg-[hsl(var(--pillot-sand))] overflow-hidden">{t.cover_url ? <img src={t.cover_url} alt={tf(t, 'title', lang)} className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]" /> : <div className="flex h-full items-center justify-center text-primary/30"><BookOpen size={40} /></div>}</div>
                    <div className="flex flex-1 flex-col p-6">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--pillot-mint))]">{t.category || 'Umum'}</span>
                      <h3 className="mt-2 font-display text-lg font-semibold leading-snug group-hover:text-primary">{tf(t, 'title', lang)}</h3>
                      <p className="mt-2 flex-1 text-sm text-muted-foreground line-clamp-3">{tf(t, 'summary', lang)}</p>
                      {t.youtube_url && <span className="mt-4 inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock size={12} />{c.lib_video}</span>}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </Section>
    </PublicLayout>
  );
}

export function TutorialPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const c = useCopy(lang);
  const [t, setT] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setT(null); api.get(`/public/landing/tutorials/${slug}`).then(({ data }) => setT(data)).catch(() => setErr(true)); }, [slug]);
  useSeo({ title: t ? `${tf(t, 'title', lang)} — Pillot` : c.seo_library_title, description: tf(t, 'summary', lang) || c.seo_library_desc, image: t?.cover_url, path: `/library/${slug}`, type: 'article', lang });
  const embed = youtubeEmbed(t?.youtube_url);
  return (
    <PublicLayout>
      <article className="mx-auto max-w-3xl px-4 sm:px-6 py-14" data-testid="tutorial-page">
        <Link to="/library" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="tutorial-back"><ArrowLeft size={14} />{c.lib_back}</Link>
        {err ? <p className="mt-8 text-muted-foreground">{c.lib_notfound}</p> : !t ? <Loader2 className="mt-8 animate-spin text-primary" /> : (
          <>
            <span className="mt-8 inline-block text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--pillot-mint))]">{t.category}</span>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight" data-testid="tutorial-title">{tf(t, 'title', lang)}</h1>
            {tf(t, 'summary', lang) && <p className="mt-4 text-base text-muted-foreground">{tf(t, 'summary', lang)}</p>}
            {embed && <div className="mt-8 aspect-video overflow-hidden rounded-2xl border border-border bg-black shadow-soft" data-testid="tutorial-video"><iframe src={embed} title={t.title} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>}
            {!embed && t.cover_url && <img src={t.cover_url} alt={t.title} className="mt-8 w-full rounded-2xl border border-border shadow-soft" />}
            <div className="mt-10"><Markdown>{tf(t, 'body_md', lang)}</Markdown></div>
          </>
        )}
      </article>
    </PublicLayout>
  );
}

export function LegalPage({ kind }) {
  const { lang } = useLang();
  const c = useCopy(lang);
  const [doc, setDoc] = useState(null);
  useEffect(() => { setDoc(null); api.get(`/public/landing/legal/${kind}`, { params: { lang } }).then(({ data }) => setDoc(data)); }, [kind, lang]);
  useSeo({ title: kind === 'terms' ? c.seo_terms_title : c.seo_privacy_title, description: kind === 'terms' ? c.terms : c.privacy, path: `/${kind}`, lang });
  return (
    <PublicLayout>
      <article className="mx-auto max-w-3xl px-4 sm:px-6 py-14" data-testid={`legal-${kind}`}>
        {!doc ? <Loader2 className="animate-spin text-primary" /> : <Markdown>{doc.body_md}</Markdown>}
      </article>
    </PublicLayout>
  );
}
